import { useAccount } from '../../../../application/hooks/useAccount';
import { Account } from '../../../../domain/models/Account';

/**
 * Extended display data not yet covered by the Account domain model.
 * This is dummy data used until real backend endpoints exist.
 */
export interface AccountDummyDetails {
  plan: string;
  mrr: number;
  currency: string;
  renewalDate: string;
  lastLoginAt: string;
  mau: number;
  healthScore: number;
  // Billing
  stripeCustomerId: string;
  paymentStatus: string;
  nextInvoiceDate: string;
  nextInvoiceAmount: number;
  invoices: Array<{ date: string; amount: number; status: string; id: string }>;
  dunningAttempts: number;
  // Integrations
  googleCalendarConnected: boolean;
  messagingProvider: string | null;
  paymentsWebhook: string | null;
  // Support
  openTickets: number;
  resolvedTickets: number;
  internalNotes: string;
  impersonationEnabled: boolean;
  auditLog: Array<{ date: string; action: string; actor: string }>;
}

const MOCK_DETAILS: AccountDummyDetails = {
  plan: 'Professional',
  mrr: 149,
  currency: 'EUR',
  renewalDate: '2025-03-15',
  lastLoginAt: '2024-11-20T09:14:00Z',
  mau: 320,
  healthScore: 87,
  stripeCustomerId: 'cus_QzXkLmNoPqRsT',
  paymentStatus: 'active',
  nextInvoiceDate: '2024-12-15',
  nextInvoiceAmount: 149,
  invoices: [
    { date: '2024-11-15', amount: 149, status: 'paid', id: 'inv_001' },
    { date: '2024-10-15', amount: 149, status: 'paid', id: 'inv_002' },
    { date: '2024-09-15', amount: 149, status: 'paid', id: 'inv_003' },
  ],
  dunningAttempts: 0,
  googleCalendarConnected: true,
  messagingProvider: 'Twilio',
  paymentsWebhook: 'https://api.glamourstudio.es/webhooks/payments',
  openTickets: 1,
  resolvedTickets: 12,
  internalNotes: 'High-value customer. Reached out about enterprise plan in October.',
  impersonationEnabled: false,
  auditLog: [
    { date: '2024-11-20', action: 'Login', actor: 'María García' },
    { date: '2024-11-18', action: 'Appointment created', actor: 'Carlos López' },
    { date: '2024-11-15', action: 'Invoice paid', actor: 'system' },
  ],
};

export interface UseAccountDetailsResult {
  account: Account | null;
  details: AccountDummyDetails | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

/**
 * Hook to fetch an account by ID with extended dummy display data.
 */
export function useAccountDetails(accountId: string | undefined): UseAccountDetailsResult {
  const {
    data: account = null,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useAccount(accountId);

  const error = !accountId
    ? 'Account ID is missing'
    : queryError instanceof Error
      ? queryError.message
      : queryError
        ? String(queryError)
        : null;

  return {
    account,
    details: account ? MOCK_DETAILS : null,
    loading,
    error,
    retry: () => {
      void refetch();
    },
  };
}
